import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import * as inst from "@/api/institutions";

// ═══════════════════════════════════════════════════════════════════
// Running a place of worship — the admin side of a temple, gurdwara or mosque.
//
// The entry point is "which ones am I part of", not a list of all of them,
// because every panel here refuses anyone without an active membership row.
// That endpoint also answers WHAT each person may do, and the spec is explicit
// about the intended use: hide what is false rather than rendering a control
// the server will refuse.
//
// Two things this page exists to make visible:
//
//   · The schedule is the aarti board. `dayType: daily` is not the same as
//     seven weekday rows, and `special` is neither — it is a one-off.
//   · The legal record NEVER gives back a tax id. You send a whole EIN and
//     read back four digits and a boolean, because it is encrypted at rest
//     and no read path exists. If a full number ever appears here, that is a
//     bug worth stopping for.
// ═══════════════════════════════════════════════════════════════════

const TABS = ["dashboard", "schedules", "photos", "admins", "followers", "legal"] as const;
type Tab = (typeof TABS)[number];

export default function InstitutionAdminLab() {
  const [id, setId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("dashboard");

  const { data, isLoading, error } = useQuery({
    queryKey: ["my-institutions"],
    queryFn: inst.getMyInstitutions,
  });

  // Bare array, not { items: [...] } — the odd one out in this module.
  const mine = data?.data ?? [];
  const current = mine.find((m) => m.id === id);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Institution admin"
        subtitle="The desk for a temple, gurdwara or mosque you help run."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Institutions", href: "/lab/institutions" },
          { label: "Admin" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-1">Where you are a member</h2>
        <p className="text-xs text-gray-500 mb-3">
          Not a list of every institution — only the ones you can actually
          administer. Everything past this point refuses a non-member.
        </p>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : error ? (
          <p className="text-sm text-red-700">
            {error instanceof Error ? error.message : "Could not read your memberships"}
          </p>
        ) : !mine.length ? (
          <p className="text-sm text-gray-500">
            You are not an admin anywhere. Nothing below can be opened — which is
            the guard working, not a broken page.
          </p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {mine.map((m) => (
              <button
                key={m.id}
                onClick={() => setId(m.id)}
                className={`px-3 py-1.5 rounded-lg text-sm border text-left ${
                  id === m.id ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                {m.name}
                <span className="text-gray-400 text-xs">
                  {" "}
                  · {m.city} · {m.myRole}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {current && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-semibold text-gray-900">{current.name}</h2>
              <Badge variant="gray">{current.myRole}</Badge>
              <Badge
                variant={current.verificationStatus === "approved" ? "green" : "yellow"}
              >
                {current.verificationStatus.replace(/_/g, " ")}
              </Badge>
            </div>
            {/* What is FALSE is not rendered as a disabled control — it is absent. */}
            <div className="flex gap-1.5 flex-wrap mt-2">
              {Object.entries(current.can)
                .filter(([, allowed]) => allowed)
                .map(([what]) => (
                  <Badge key={what} variant="blue">
                    {what.replace(/([A-Z])/g, " $1").toLowerCase()}
                  </Badge>
                ))}
            </div>
            <div className="flex gap-1 flex-wrap mt-3 border-b border-gray-200 pb-3">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                    tab === t ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {tab === "dashboard" && <Dashboard id={current.id} />}
          {tab === "schedules" && <Schedules id={current.id} />}
          {tab === "photos" && <Photos id={current.id} />}
          {tab === "admins" && <Admins id={current.id} canInvite={current.can.inviteAdmins} />}
          {tab === "followers" && <Followers id={current.id} />}
          {tab === "legal" && <Legal id={current.id} />}
        </section>
      )}
    </div>
  );
}

function useAction(keys: string[]) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      await fn();
      if (ok) setNote(ok);
      for (const k of keys) await qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      // A permission refusal is content here, not a failure to hide.
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const banner = (
    <>
      {note && <p className="text-sm text-green-700 mt-2">{note}</p>}
      {err && <p className="text-sm text-red-700 mt-2">{err}</p>}
    </>
  );
  return { run, busy, banner };
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

// ── Dashboard ──────────────────────────────────────────────────────

function Dashboard({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["inst-dashboard", id],
    queryFn: () => inst.getInstitutionDashboard(id),
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  const d = data?.data;
  if (!d) return <Empty>No dashboard.</Empty>;

  return (
    <div className="space-y-4">
      <div className="flex gap-6 flex-wrap">
        <Stat label="followers" value={d.stats.followerCount} />
        <Stat label="upcoming events" value={d.stats.upcomingEventsCount} />
        <Stat label="open opportunities" value={d.stats.openOpportunities} />
        <Stat label="volunteers waiting" value={d.stats.pendingVolunteers} />
      </div>
      <p className="text-xs text-gray-500">
        Volunteers waiting is the only number here that asks for something. The
        rest describe the place; that one names a queue somebody is sitting in.
      </p>
      <div className="flex gap-6 flex-wrap border-t border-gray-200 pt-3">
        <Stat label="events all time" value={d.institution.eventCount} />
        <Stat label="volunteers all time" value={d.institution.volunteerCount} />
        <Stat label="total attendance" value={d.institution.totalAttendance} />
      </div>
    </div>
  );
}

// ── Schedules ──────────────────────────────────────────────────────

function Schedules({ id }: { id: string }) {
  const [day, setDay] = useState<string>("daily");
  const [name, setName] = useState("");
  const [start, setStart] = useState("07:00");
  const { run, busy, banner } = useAction(["inst-schedules"]);

  const { data, isLoading } = useQuery({
    queryKey: ["inst-schedules", id],
    queryFn: () => inst.listInstitutionSchedules(id),
  });
  const items = data?.data.items ?? [];

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        The aarti board. <code>daily</code> is one row that means every day —
        not seven weekday rows — and <code>special</code> is neither, it is a
        one-off. Times are strictly HH:MM; the server matches the pattern.
      </p>

      <div className="mb-4 flex gap-2 flex-wrap items-center">
        <select
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        >
          {inst.SCHEDULE_DAYS.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Morning Aarti"
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[10rem]"
        />
        <input
          type="time"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        <Button
          size="sm"
          isLoading={busy}
          disabled={!name.trim()}
          onClick={() =>
            run(async () => {
              await inst.createInstitutionSchedule(id, {
                dayType: day,
                activityName: name.trim(),
                startTime: start,
              });
              setName("");
            }, "Added to the board.")
          }
        >
          Add
        </Button>
        {banner}
      </div>

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !items.length ? (
        <Empty>Nothing on the board yet.</Empty>
      ) : (
        <ul className="space-y-2">
          {items.map((s) => (
            <li
              key={s.id}
              className="border border-gray-200 rounded-xl px-4 py-2.5 flex items-center gap-2 flex-wrap text-sm"
            >
              <Badge variant={s.dayType === "daily" ? "green" : "gray"}>{s.dayType}</Badge>
              <span className="font-medium text-gray-900">{s.activityName}</span>
              <span className="tabular-nums text-gray-600">
                {s.startTime}
                {s.endTime && `–${s.endTime}`}
              </span>
              {s.language && <Badge variant="blue">{s.language}</Badge>}
              {!s.isPublic && <Badge variant="yellow">private</Badge>}
              <span className="ml-auto flex gap-2">
                <button
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => inst.updateInstitutionSchedule(id, s.id, { isPublic: !s.isPublic }),
                      s.isPublic ? "Hidden from visitors." : "Shown to visitors.",
                    )
                  }
                  className="text-xs text-gray-600 disabled:opacity-50"
                >
                  {s.isPublic ? "hide" : "show"}
                </button>
                <button
                  disabled={busy}
                  onClick={() => run(() => inst.deleteInstitutionSchedule(id, s.id), "Removed.")}
                  className="text-xs text-red-700 disabled:opacity-50"
                >
                  delete
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Photos ─────────────────────────────────────────────────────────

function Photos({ id }: { id: string }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const { run, busy, banner } = useAction(["inst-photos"]);

  const { data, isLoading } = useQuery({
    queryKey: ["inst-photos", id],
    queryFn: () => inst.listInstitutionPhotos(id),
  });
  const items = data?.data.items ?? [];

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        Takes a URL, not a file — uploading happens elsewhere and hands you one.
        Exactly one photo is primary; marking a new one moves the flag.
      </p>
      <div className="mb-4 flex gap-2 flex-wrap items-center">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…/mandir.jpg"
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[14rem]"
        />
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional)"
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        <Button
          size="sm"
          isLoading={busy}
          disabled={!url.trim().startsWith("http")}
          onClick={() =>
            run(async () => {
              await inst.createInstitutionPhoto(id, {
                photoUrl: url.trim(),
                caption: caption.trim() || undefined,
              });
              setUrl("");
              setCaption("");
            }, "Photo added.")
          }
        >
          Add
        </Button>
        {banner}
      </div>

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !items.length ? (
        <Empty>No photos.</Empty>
      ) : (
        <ul className="space-y-2">
          {items.map((p) => (
            <li
              key={p.id}
              className="border border-gray-200 rounded-xl px-4 py-2.5 flex items-center gap-2 flex-wrap text-sm"
            >
              {p.isPrimary && <Badge variant="green">primary</Badge>}
              {p.category && <Badge variant="gray">{p.category}</Badge>}
              <span className="text-gray-900">{p.caption ?? "(no caption)"}</span>
              <code className="text-xs text-gray-400 truncate max-w-[16rem]">{p.photoUrl}</code>
              <button
                disabled={busy}
                onClick={() => run(() => inst.deleteInstitutionPhoto(id, p.id), "Removed.")}
                className="text-xs text-red-700 ml-auto disabled:opacity-50"
              >
                delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Admins ─────────────────────────────────────────────────────────

function Admins({ id, canInvite }: { id: string; canInvite: boolean }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const { run, busy, banner } = useAction(["inst-admins"]);

  const { data, isLoading } = useQuery({
    queryKey: ["inst-admins", id],
    queryFn: () => inst.listInstitutionAdmins(id),
  });
  const items = data?.data.items ?? [];

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        Carries each admin's <strong>email</strong> — an admin-only surface, like
        the event attendee list.
      </p>

      {/* Absent, not disabled, when the permission is false. */}
      {canInvite && (
        <div className="mb-4 flex gap-2 flex-wrap items-center">
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="pujari@example.org"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
          <input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="role"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-32"
          />
          <Button
            size="sm"
            isLoading={busy}
            disabled={!email.includes("@")}
            onClick={() =>
              run(async () => {
                await inst.inviteInstitutionAdmin(id, email.trim(), role.trim());
                setEmail("");
              }, "Invited.")
            }
          >
            Invite
          </Button>
          {banner}
        </div>
      )}

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !items.length ? (
        <Empty>No admins listed.</Empty>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li
              key={m.id}
              className="border border-gray-200 rounded-xl px-4 py-2.5 flex items-center gap-2 flex-wrap text-sm"
            >
              <span className="font-medium text-gray-900">{m.user.name}</span>
              <span className="text-xs text-gray-500">{m.user.email}</span>
              <Badge variant="blue">{m.role}</Badge>
              {m.title && <Badge variant="gray">{m.title}</Badge>}
              <span className="ml-auto flex gap-2">
                <button
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => inst.updateInstitutionAdmin(id, m.id, { title: "Trustee" }),
                      "Title set.",
                    )
                  }
                  className="text-xs text-gray-600 disabled:opacity-50"
                >
                  set title
                </button>
                <button
                  disabled={busy}
                  onClick={() => run(() => inst.removeInstitutionAdmin(id, m.id), "Removed.")}
                  className="text-xs text-red-700 disabled:opacity-50"
                  title="Removing the last admin should be refused"
                >
                  remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Followers ──────────────────────────────────────────────────────

function Followers({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["inst-followers", id],
    queryFn: () => inst.listInstitutionFollowers(id, { limit: 50 }),
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  const items = data?.data.items ?? [];
  const meta = data?.data.meta;
  if (!items.length) return <Empty>Nobody follows this yet.</Empty>;

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        {meta?.total} in total. Each carries what they asked to hear about, and
        those are three separate switches — following is not consent to
        everything.
      </p>
      <ul className="space-y-2">
        {items.map((f) => (
          <li
            key={f.id}
            className="border border-gray-200 rounded-xl px-4 py-2.5 flex items-center gap-2 flex-wrap text-sm"
          >
            <span className="font-medium text-gray-900">{f.user.name}</span>
            {f.user.city && <span className="text-xs text-gray-500">{f.user.city}</span>}
            <span className="ml-auto flex gap-1.5">
              {f.notifyEvents && <Badge variant="gray">events</Badge>}
              {f.notifyAnnouncements && <Badge variant="gray">announcements</Badge>}
              {f.notifyVolunteerOpps && <Badge variant="gray">volunteering</Badge>}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

// ── Legal ──────────────────────────────────────────────────────────

function Legal({ id }: { id: string }) {
  const [name, setName] = useState("");
  const [ein, setEin] = useState("");
  const { run, busy, banner } = useAction(["inst-legal"]);

  const { data, isLoading } = useQuery({
    queryKey: ["inst-legal", id],
    queryFn: () => inst.getInstitutionLegal(id),
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  const state = data?.data;
  const legal = state?.legal;

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        The tax id is <strong>write-only</strong>. You send a whole EIN and read
        back four digits and a yes/no, because it is encrypted at rest and there
        is deliberately no path that returns it. A full number appearing here
        would be a bug worth stopping for.
      </p>

      {state && !state.einCaptureEnabled && (
        <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-3">
          This server holds no encryption key, so an EIN sent now is silently
          SKIPPED rather than stored. Everything else on this form still saves.
        </p>
      )}

      <div className="space-y-2 text-sm mb-4">
        <div>
          <span className="text-gray-500">Legal name: </span>
          {legal?.legalEntityName ?? <span className="text-gray-400">not recorded</span>}
        </div>
        <div>
          <span className="text-gray-500">Entity type: </span>
          {legal?.entityType?.replace(/_/g, " ") ?? (
            <span className="text-gray-400">not recorded</span>
          )}
        </div>
        <div>
          <span className="text-gray-500">Tax id: </span>
          {legal?.hasEin ? (
            <span className="tabular-nums">••••{legal.einLast4}</span>
          ) : (
            <span className="text-gray-400">none on file</span>
          )}
        </div>
        {legal?.primaryContactName && (
          <div>
            <span className="text-gray-500">Contact: </span>
            {legal.primaryContactName}
            {legal.primaryContactEmail && ` · ${legal.primaryContactEmail}`}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 pt-4 flex gap-2 flex-wrap items-center">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Legal entity name"
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[12rem]"
        />
        <input
          value={ein}
          onChange={(e) => setEin(e.target.value)}
          placeholder="EIN (write-only)"
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-40"
        />
        <Button
          size="sm"
          isLoading={busy}
          disabled={!name.trim() && !ein.trim()}
          onClick={() =>
            run(async () => {
              await inst.updateInstitutionLegal(id, {
                legalEntityName: name.trim() || undefined,
                ein: ein.trim() || undefined,
              });
              setEin("");
            }, "Saved — read it back and only the last four return.")
          }
        >
          Save
        </Button>
        {banner}
      </div>
    </>
  );
}
